import React, {ReactElement} from 'react';
import './external/animate.css';
import {useSelector} from 'react-redux';
import {BrowserRouter, Navigate, Route, Routes} from 'react-router-dom';
import {Slide, toast, ToastContainer} from 'react-toastify';
import {AuthRedirectRoute} from './components/router/AuthRedirectRoute';
import {ProtectedRoute} from './components/router/ProtectedRoute';
import {UserProtectedRoute} from './components/router/UserProtectedRoute';
import {Role} from './incase-domain/user';
import {errorStatusSelector} from './store/selectors/error.selectors';
import {pageSpinnerSelector, windowResizeSpinnerSelector} from './store/selectors/spinner.selector';
import ErrorReport from './ui/components/ErrorReport';
import SpinnerModal from './ui/components/SpinnerModal';
import {CustomCloseButton} from './ui/components/Toast';
import AuthLayout from './ui/layout/AuthLayout';
import BaseLayout from './ui/layout/BaseLayout';
import EditorLayout from './ui/layout/EditorLayout';
import ForgotPasswordPage from './ui/pages/auth/ForgotPasswordPage';
import LoginPage from './ui/pages/auth/LoginPage';
import ResetPasswordPage from './ui/pages/auth/ResetPasswordPage';
import ConnectionsPage from './ui/pages/connections/ConnectionsPage';
import DashboardPage from './ui/pages/dashboard/DashboardPage';
import EditorPage from './ui/pages/editorPage/EditorPage';
import EditorDefaultPage from './ui/pages/editorPage/parts/EditorDefaultPage';
import EditorSharedPresentationsPage from './ui/pages/editorPage/parts/EditorSharedPresentationsPage';
import HomePage from './ui/pages/homePage/HomePage';
import HomeDefaultPage from './ui/pages/homePage/parts';
import HomeFeedPage from './ui/pages/homePage/parts/HomeFeedPage';
import MessagesPage from './ui/pages/messages/MessagesPage';
import NotificationsPage from './ui/pages/NotificationsPage/NotificationsPage';
import PresentationEditPage from './ui/pages/PresEditPage/PresentationEditPage';
import PresentationReportsPage from './ui/pages/PresentationReportsPage/PresentationReportsPage';
import CreatePresentationThemePage from './ui/pages/PresentationThemePage/CreateTheme/CreatePresentationThemePage';
import EditPresentationThemePage from './ui/pages/PresentationThemePage/EditTheme/EditPresentationThemePage';
import RoomsPage from './ui/pages/rooms/RoomsPage';
import SettingsPage from './ui/pages/SettingsPage';
import UsersPage from './ui/pages/usersPage/UsersPage';
import {getCssVariable} from './utils/style.utils';
import GeneratorPage from './ui/pages/generator/GeneratorPage';
import BlankLayout from './ui/layout/BlankLayout';

function App(): ReactElement {
    const pageSpinner = useSelector(pageSpinnerSelector);
    const windowResizeSpinner = useSelector(windowResizeSpinnerSelector);
    const errorStatus = useSelector(errorStatusSelector);

    return (
        <div>
            <SpinnerModal active={pageSpinner || windowResizeSpinner} />

            <>
                <ToastContainer
                    position={toast.POSITION.BOTTOM_LEFT}
                    theme="light"
                    limit={6}
                    transition={Slide}
                    autoClose={5000}
                    hideProgressBar
                    newestOnTop={false}
                    closeOnClick={false}
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    style={{zIndex: getCssVariable('--z-index-toast')}}
                    closeButton={CustomCloseButton}
                />
                <BrowserRouter>
                    {errorStatus ? (
                        <ErrorReport />
                    ) : (
                        <Routes>
                            <Route element={<AuthLayout />}>
                                <Route
                                    path="/login"
                                    element={
                                        <AuthRedirectRoute>
                                            <LoginPage />
                                        </AuthRedirectRoute>
                                    }
                                />
                                <Route path="/forgotPassword" element={<ForgotPasswordPage />} />
                                <Route path="/resetPassword/:code" element={<ResetPasswordPage />} />
                            </Route>
                            <Route element={<BlankLayout />}>
                                <Route
                                    path="/presentations/:presentationId/report"
                                    element={
                                        <ProtectedRoute roles={[Role.SUPERUSER, Role.ADMIN]}>
                                            <PresentationReportsPage />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>
                            <Route
                                element={
                                    <>
                                        <BaseLayout />
                                    </>
                                }
                            >
                                <Route
                                    path="/users"
                                    element={
                                        <ProtectedRoute roles={[Role.SUPERUSER, Role.ADMIN]}>
                                            <UsersPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/presentations"
                                    element={
                                        <UserProtectedRoute>
                                            <EditorPage />
                                        </UserProtectedRoute>
                                    }
                                >
                                    <Route index element={<EditorDefaultPage />} />
                                    <Route path="shared" element={<EditorSharedPresentationsPage />} />
                                </Route>
                                <Route
                                    path="/settings"
                                    element={
                                        <UserProtectedRoute>
                                            <SettingsPage />
                                        </UserProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/connections"
                                    element={
                                        <UserProtectedRoute>
                                            <ConnectionsPage />
                                        </UserProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/notifications"
                                    element={
                                        <UserProtectedRoute>
                                            <NotificationsPage />
                                        </UserProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/messages/:conversationId?"
                                    element={
                                        <UserProtectedRoute>
                                            <MessagesPage />
                                        </UserProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/rooms"
                                    element={
                                        <UserProtectedRoute>
                                            <RoomsPage />
                                        </UserProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/newTheme"
                                    element={
                                        <UserProtectedRoute>
                                            <CreatePresentationThemePage />
                                        </UserProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/editTheme/:themeId"
                                    element={
                                        <UserProtectedRoute>
                                            <EditPresentationThemePage />
                                        </UserProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard"
                                    element={
                                        <UserProtectedRoute>
                                            <DashboardPage />
                                        </UserProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/generator"
                                    element={
                                        <UserProtectedRoute>
                                            <GeneratorPage />
                                        </UserProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/"
                                    element={
                                        <UserProtectedRoute>
                                            <HomePage />
                                        </UserProtectedRoute>
                                    }
                                >
                                    <Route path="/" element={<HomeDefaultPage />} />
                                    <Route path="/feed" element={<HomeFeedPage />} />
                                </Route>
                                <Route path="/*" element={<Navigate replace to="/" />} />
                            </Route>
                            <Route element={<EditorLayout />}>
                                <Route
                                    path="/presentations/:presentationId"
                                    element={
                                        <UserProtectedRoute>
                                            <PresentationEditPage />
                                        </UserProtectedRoute>
                                    }
                                />
                            </Route>
                        </Routes>
                    )}
                </BrowserRouter>
            </>
        </div>
    );
}

export default App;
